import {IcnArrowLeft, IcnArrowRight} from "../../assets/icons";

import {ReactNode, useState} from "react"
import clsx from "clsx"
import type { ColumnDef } from '@tanstack/react-table'
import {useReactTable, getCoreRowModel, flexRender, getPaginationRowModel, PaginationState} from '@tanstack/react-table'


import Button from "./Button"
import Skeleton from "react-loading-skeleton";

interface TableProps<T extends object> {
  extraClassName?: string;
  data: T[];
  columns: ColumnDef<T>[];
  tableHasCaption?: boolean;
  tableCaptionTitle?: string;
  customDefaultEmpty?: boolean;
  defaultEmptyHeading?: string;
  defaultEmptyTitle?: string;
  defaultEmptyExtraContent?: ReactNode;
  removePagination?: boolean;
  pageSize?: number;
  isLoading?: boolean;
}

function Table<T extends object>(
  {
    extraClassName,
    data,
    columns,
    tableHasCaption,
    tableCaptionTitle,
    customDefaultEmpty,
    defaultEmptyHeading,
    defaultEmptyTitle,
    defaultEmptyExtraContent,
    removePagination,
    pageSize = 5,
    isLoading,
  }: TableProps<T>) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  })

  const totalPageCount = Math.ceil(table.getCoreRowModel().rows.length / pagination.pageSize)

  //No Data created by the user
  const allCellsEmpty = table
    .getRowModel()
    .rows.every((row) =>
      row.getVisibleCells().every((cell) => {
        const cellValue = flexRender(
          cell.column.columnDef.cell,
          cell.getContext()
        )
        return !cellValue || cellValue === ""
      })
    )

  //Loading state
  const renderSkeletonRows = () => {
    return Array.from({ length: pageSize }).map((_, index) => (
      <tr key={`skeleton-${index}`}>
        {columns.map((column, cellIndex) => (
          <td key={`skeleton-cell-${cellIndex}`} className="c-table-td-loading">
            <Skeleton
              baseColor="var(--light-grey-50)"
              highlightColor={"var(--white)"}
            />
          </td>
        ))}
      </tr>
    ))
  }

  const getPaginationModel = () => {
    const pageCount = totalPageCount;
    const currentPage = pagination.pageIndex;
    let pageNumbers: number[] = [];

    if (pageCount <= 7) {
      pageNumbers = Array.from({ length: pageCount }, (_, i) => i);
    } else {
      pageNumbers.push(0);

      if (currentPage < 3) {
        pageNumbers.push(1, 2, 3, 4);
        pageNumbers.push(-1);
      } else if (currentPage > pageCount - 4) {
        pageNumbers.push(-1);
        for (let i = pageCount - 5; i < pageCount; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(-1);
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        if (currentPage + 2 < pageCount - 1) {
          pageNumbers.push(-1);
        }
      }

      if (pageNumbers[pageNumbers.length - 1] !== pageCount - 1) {
        pageNumbers.push(pageCount - 1);
      }
    }

    return pageNumbers.filter((value, index, self) => self.indexOf(value) === index);
  };

  return (
    <>
      <div className={clsx("c-table-wrapper", allCellsEmpty && "default-empty")}>

        <table className={clsx("c-table", extraClassName && extraClassName)}>

          {tableHasCaption && (
            <caption>
              <div>{tableCaptionTitle}</div>
            </caption>
          )}

          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {isLoading? (
            <tbody>
              {renderSkeletonRows()}
            </tbody>
          ) : allCellsEmpty ? (
            <>
              <tbody className='c-table-tbody-empty'>
                {Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="c-table-tbody-empty-skeleton-tr">
                    {columns.map((column, cellIndex) => (
                      <td key={`skeleton-cell-${cellIndex}`} className="c-table-tbody-empty-skeleton-td">
                        <Skeleton
                          enableAnimation={true}
                          baseColor="var(--light-grey-50)"
                          highlightColor={"var(--white)"}
                        />
                      </td>
                    ))}
                  </tr>
                ))}

                <tr className="c-table-tbody-empty-tr">

                  <td colSpan={columns.length} className="c-table-tbody-empty-tr-td">

                    <div className="c-table-tbody-empty-tr-td-wrapper">

                      <div className="c-table-tbody-empty-tr-td-text">
                        {customDefaultEmpty ? (
                          <>
                            {defaultEmptyHeading && (<small>{defaultEmptyHeading}</small>)}
                            {defaultEmptyTitle && (<p className="h5">{defaultEmptyTitle}</p>)}
                            {defaultEmptyExtraContent && defaultEmptyExtraContent}
                          </>
                        ):(
                          <>
                            <small>No data found</small>
                            <h3 className="h5">There is no data available at this moment</h3>
                          </>
                        )}
                      </div>

                    </div>

                  </td>

                </tr>

              </tbody>
            </>
          ): (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}

        </table>

      </div>

      {!removePagination && (
        <div className="c-table-pagination">

          <div className="c-table-pagination-page-index">

            <div className="c-button-container">
              {getPaginationModel().map((pageIndex) => (
                <Button
                  key={pageIndex}
                  extraClassName={clsx("c-table-pagination-btn index", table.getState().pagination.pageIndex === pageIndex && "is-active",)}
                  btnColor="white-outline"
                  title={`Page ${pageIndex + 1}`}
                  icon={pageIndex === -1 ? "..." : pageIndex + 1}
                  onClick={() => table.setPageIndex(pageIndex)}
                  disabled={totalPageCount <= 1}
                />
              ))}

              {totalPageCount <= 1 && (
                <Button btnColor='white-outline' icon="..."  disabled />
              )}

            </div>

          </div>

          <div className="c-button-container">

            <Button
              extraClassName="c-table-pagination-btn previous"
              btnVariant="icon"
              btnColor="white-outline"
              title="Previous"
              icon={<IcnArrowLeft/>}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            />

            <Button
              extraClassName="c-table-pagination-btn next"
              btnVariant="icon"
              btnColor="white-outline"
              icon={<IcnArrowRight/>}
              title="Next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            />

          </div>

        </div>

      )}
    </>
  )
}

export default Table
